/**
 * Rasterizer entry — engine selection + fallback.
 *
 * Primary: snapdom (native-fidelity SVG-foreignObject rendering).
 * Fallback: html2canvas-pro (independent code path).
 *
 * A snapdom failure is logged as a WARN to the file's debug log (the export
 * continues on the fallback); a fallback failure is the caller's to report as
 * the terminal error. Force an engine for support/debugging with
 * `localStorage['papan:export-engine'] = 'snapdom' | 'html2canvas'`.
 */
import { ExportFault, reportExportError } from '../export-errors.js'
import { snapdomEngine } from './snapdom-engine.js'
import { CAPTURE_TIMEOUT_MS, type RasterizeRequest, type RasterSource } from './types.js'

// The fallback engine is a rarely-taken path — snapdom serves ~every capture, and
// html2canvas-pro is only reached on a snapdom failure or a forced override. It's
// its own ~46 KB-gzip chunk, so import it lazily: that weight stays off every
// export and is fetched only the moment the fallback is actually needed.
const loadHtml2canvasEngine = () => import('./html2canvas-engine.js').then((m) => m.html2canvasEngine)

export type { RasterSource, RasterizeRequest, Region, RasterEngineName } from './types.js'
export { MAX_CANVAS_DIM, MAX_SOURCE_HEIGHT } from './types.js'

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new ExportFault(`${label}: capture timed out after ${ms}ms`, 'rasterize-timeout', 'rasterize')), ms)
    p.then((v) => { clearTimeout(t); resolve(v) }, (e) => { clearTimeout(t); reject(e) })
  })
}

function forcedEngine(): 'snapdom' | 'html2canvas' | null {
  try {
    const v = localStorage.getItem('papan:export-engine')
    return v === 'snapdom' || v === 'html2canvas' ? v : null
  } catch {
    return null
  }
}

export async function rasterize(req: RasterizeRequest, ctx: { fileId: string }): Promise<RasterSource> {
  const forced = forcedEngine()
  if (forced === 'html2canvas') return (await loadHtml2canvasEngine()).capture(req)
  if (forced === 'snapdom') return withTimeout(snapdomEngine.capture(req), CAPTURE_TIMEOUT_MS, 'snapdom')

  try {
    return await withTimeout(snapdomEngine.capture(req), CAPTURE_TIMEOUT_MS, 'snapdom')
  } catch (e) {
    // Degraded, not failed: the export continues on the fallback engine.
    reportExportError('rasterize', e, { fileId: ctx.fileId, engine: 'snapdom', level: 'warn' })
    return (await loadHtml2canvasEngine()).capture(req)
  }
}
